"use client";

import { Card, CardContent } from "@/components/ui/card";
import clsx from "clsx";
import {
  ArrowRight,
  Banknote,
  Calculator,
  Clock3,
  CreditCard,
  Database,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  Sparkles,
} from "lucide-react";
import Link from "next/link";

function getPayrollPeriodLabel(date: Date) {
  const day = date.getDate();

  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  if (day < 16) {
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }

  return `${String(month).padStart(2, "0")}/${year}`;
}

const sections = [
  {
    eyebrow: "Văn phòng tính lương",
    title: "Xử lý dữ liệu cốt lõi",
    description:
      "Hồ sơ lương, công thức và tham số cần can thiệp trước khi chốt kỳ.",
    items: [
      {
        href: "/payroll/profiles",
        title: "Hồ sơ lương",
        desc: "Quản lý profile lương cho nhân viên và đối tượng tính lương.",
        icon: Banknote,
        color: "primary",
      },
      {
        href: "/payroll/queries",
        title: "Công thức lương",
        desc: "Theo dõi biểu thức tính lương và logic nghiệp vụ.",
        icon: Calculator,
        color: "indigo",
      },
      {
        href: "/payroll/payroll-parameters",
        title: "Tham số lương",
        desc: "Quản lý biến đầu vào, hệ số và thông số sử dụng trong kỳ.",
        icon: Database,
        color: "indigo",
      },
    ],
  },
  {
    eyebrow: "Nguồn đầu vào",
    title: "Dữ liệu nghiệp vụ",
    description:
      "Nhanh tay vào các bảng dữ liệu cần cập nhật trước khi tính lương.",
    items: [
      {
        href: "/data/timesheets",
        title: "Chấm công",
        desc: "Dữ liệu công nhân viên và xử lý danh sách theo kỳ.",
        icon: Clock3,
        color: "emerald",
      },
      {
        href: "/data/team-incomes",
        title: "Lương khoán",
        desc: "Doanh thu đội nhóm, nghiệm thu và phân bổ khoán.",
        icon: FolderKanban,
        color: "emerald",
      },
    ],
  },
  {
    eyebrow: "Đầu ra và đối soát",
    title: "Báo cáo và thanh toán",
    description:
      "Tổng hợp kết quả tính lương, đối chiếu và phát hành danh sách thanh toán.",
    items: [
      {
        href: "/reports/salaries",
        title: "Báo cáo lương",
        desc: "Tổng hợp và đối chiếu số liệu lương theo kỳ.",
        icon: FileText,
        color: "amber",
      },
      {
        href: "/reports/bank-transfer-sheets",
        title: "Chuyển khoản",
        desc: "Danh sách chuyển khoản ngân hàng và đối soát thanh toán.",
        icon: CreditCard,
        color: "amber",
      },
      {
        href: "/reports/issue-payslips",
        title: "Phát hành phiếu lương",
        desc: "In, đối chiếu và gửi phiếu lương cho nhân viên.",
        icon: FileSpreadsheet,
        color: "primary",
      },
    ],
  },
] satisfies Array<{
  eyebrow: string;
  title: string;
  description: string;
  items: Array<{
    href: string;
    title: string;
    desc: string;
    icon: any;
    color: "primary" | "indigo" | "emerald" | "amber";
  }>;
}>;

export default function DashboardPage() {
  const payrollPeriod = getPayrollPeriodLabel(new Date());

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-[28px] border border-slate-200/80 surface-hero p-6 shadow-raised lg:p-8 dark:border-slate-700/80">
        <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-[radial-gradient(circle,hsl(var(--primary-alt)/0.13)_0%,transparent_68%)]" />
        <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-[radial-gradient(circle,hsl(var(--secondary-alt)/0.13)_0%,transparent_72%)]" />

        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)] lg:items-end">
          <div className="space-y-4">
            <div className="border-[var(--primary-alt)]/12 bg-white/78 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-[0.08em] text-[var(--primary-alt)] shadow-sm dark:bg-slate-800/78 dark:border-[var(--primary-alt)]/20">
              <Sparkles className="size-3" />
              Payroll hub
            </div>

            <div className="space-y-2">
              <h1 className="max-w-3xl text-3xl font-semibold tracking-tight text-slate-950 lg:text-4xl dark:text-slate-50">
                Bắt đầu kỳ lương nhanh hơn với các luồng công việc chính.
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-600 lg:text-[15px] dark:text-slate-400">
                Dashboard được rút gọn để tập trung vào điều hướng và thao tác
                thường xuyên, thay vì hiển thị các block trạng thái chưa có dữ
                liệu.
              </p>
            </div>
          </div>

          <Card className="border-white/70 bg-white/85 shadow-raised dark:border-slate-700/70 dark:bg-slate-800/85">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                    Kỳ lương đang thao tác
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-slate-950 dark:text-slate-50">
                    {payrollPeriod}
                  </div>
                </div>
                <div className="bg-[var(--primary-alt)]/10 rounded-2xl p-3 text-[var(--primary-alt)]">
                  <Banknote className="size-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-3">
        {sections.map((section) => (
          <Card
            key={section.title}
            className="bg-white/88 overflow-hidden border-slate-200/80 shadow-raised dark:border-slate-700/80 dark:bg-slate-800/88 dark:shadow-none"
          >
            <CardContent className="space-y-5 p-5">
              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                  {section.eyebrow}
                </div>
                <div className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-100">
                  {section.title}
                </div>
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-400">
                  {section.description}
                </p>
              </div>

              <div className="space-y-2">
                {section.items.map((item) => (
                  <ShortcutCard key={item.href} {...item} />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
}

function ShortcutCard({
  href,
  title,
  desc,
  icon: Icon,
  color,
}: {
  href: string;
  title: string;
  desc: string;
  icon: any;
  color: "primary" | "indigo" | "emerald" | "amber";
}) {
  const colorMap = {
    primary:
      "bg-[linear-gradient(135deg,rgba(43,88,194,0.12),rgba(91,140,255,0.08))] text-[var(--primary-alt)]",
    indigo: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-500/12 text-amber-600 dark:text-amber-400",
  };

  return (
    <Link
      href={href}
      className="hover:border-[var(--primary-alt)]/20 group block rounded-[22px] border border-slate-200/80 surface-tile p-4 transition hover:-translate-y-0.5 hover:shadow-raised dark:border-slate-700/80 dark:hover:border-[var(--primary-alt)]/30"
    >
      <div className="flex items-start gap-4">
        <div
          className={clsx(
            "flex size-12 shrink-0 items-center justify-center rounded-2xl",
            colorMap[color]
          )}
        >
          <Icon className="size-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </div>
            <ArrowRight className="mt-0.5 size-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-[var(--primary-alt)] dark:text-slate-600" />
          </div>
          <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-400">{desc}</p>
        </div>
      </div>
    </Link>
  );
}
